import React from 'react';
import profile from '../data/profile.json';
import { FaLinkedin, FaGithub } from 'react-icons/fa';
import { useI18n } from '../i18n';

const ProfileSection = () => {
  const { name, headline, location, summary, linkedin, github, avatar, skills, projects, certifications, education } = profile;
  const { t } = useI18n();

  return (
    <section className="ccc-section bg-white/70">
      <div className="ccc-container">
        <div className="ccc-section-head">
          <div>
            <p className="ccc-eyebrow mb-4">{t('ui.operatorProfile')}</p>
            <h2 className="ccc-section-title">{name}</h2>
          </div>
          <p className="ccc-section-copy">{headline}</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(18rem,0.35fr)_minmax(0,1fr)]">
          <aside className="ccc-ledger p-6">
            {avatar ? (
              <img src={avatar} alt={`${name} avatar`} className="h-44 w-full object-cover grayscale" />
            ) : (
              <div className="ccc-logo-tile !min-h-0 h-44">
                <img
                  src="/images/badges/crespo-mark.png"
                  alt="Crespo Cyber Consulting logo"
                  className="max-h-24 max-w-[8rem] object-contain"
                />
              </div>
            )}
            <div className="mt-5">
              <p className="ccc-label">{t('ui.location')}</p>
              <p className="ccc-value mt-2">{location}</p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3 border-t border-slate-200 pt-5">
              {linkedin && (
                <a href={linkedin} target="_blank" rel="noopener noreferrer" className="ccc-outline-btn bg-white">
                  <FaLinkedin />
                  LinkedIn
                </a>
              )}
              {github && (
                <a href={github} target="_blank" rel="noopener noreferrer" className="ccc-outline-btn bg-white">
                  <FaGithub />
                  GitHub
                </a>
              )}
            </div>
          </aside>

          <div className="ccc-ledger p-6 md:p-8">
            <div className="ccc-ledger-row !border-t-0 !pt-0">
              <span className="ccc-label">{t('profile.summary')}</span>
              <p className="ccc-work-copy">{summary}</p>
            </div>

            {skills?.length > 0 && (
              <div className="ccc-ledger-row">
                <span className="ccc-label">{t('profile.skills')}</span>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill) => (
                    <span key={skill} className="ccc-pill">{skill}</span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(education) && education.length > 0 && (
              <div className="ccc-ledger-row">
                <span className="ccc-label">{t('profile.education')}</span>
                <ul className="space-y-3">
                  {education.map((item) => (
                    <li key={`${item.degree}-${item.school}`} className="ccc-value">
                      {item.degree}
                      <span className="block text-sm font-medium text-slate-500">
                        {item.school}{item.year ? ` / ${item.year}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {projects?.length > 0 && (
              <div className="ccc-ledger-row">
                <span className="ccc-label">{t('profile.projects')}</span>
                <div className="grid gap-3">
                  {projects.map((project) => (
                    <div key={project.name} className="border border-slate-200 bg-white p-4">
                      <p className="font-black text-slate-950">{project.name}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">{project.description}</p>
                      {project.link && (
                        <a href={project.link} className="ccc-link mt-3 inline-block text-sm">View</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(certifications) && certifications.length > 0 && (
              <div className="ccc-ledger-row">
                <span className="ccc-label">{t('profile.certifications')}</span>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {certifications.map((cert) => (
                    <li key={cert.name} className="border border-slate-200 bg-white p-4">
                      <p className="text-sm font-black leading-snug text-slate-950">{cert.name}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {cert.issuer}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfileSection;
